import {
  ColumnSortEnum,
  TableStateWithFilters,
} from "@king-ict/eupisi-ui/components";

import { PersonIdentification } from "../../../models";
import {
  EnrollmentDecisionSolution,
  Form7,
  RequestDataSearch,
  RequestSort,
} from "../../../models/Request";
import { ClaimType, UserPermission } from "../../../models/User";
import { TableUtils } from "../../../utils";

export const createInitialRequestDataSearch = ({
  schoolYearId,
  initialTableState,
  defaultInstitutionIds,
}: {
  schoolYearId?: number;
  initialTableState: TableStateWithFilters<RequestDataSearch>;
  defaultInstitutionIds: number[];
}): RequestDataSearch => {
  const {
    institutionIds,
    code,
    firstName,
    lastName,
    oib,
    requestType,
    requestStatus,
    onlyRequests,
    onlyWithAddReasonDelayPreviousYear,
    onlyNotSchoolCandidate,
    onlyLanguageCourseRequired,
    onlyEnrollmentApproved,
    onlyEnrolled,
    outsideEnrollmentAreaStatus,
    onlyOutsideEnrollmentArea,
  } = initialTableState;

  return {
    schoolYearId,
    institutionIds: institutionIds ? institutionIds : defaultInstitutionIds,
    code,
    firstName,
    lastName,
    oib,
    requestType,
    requestStatus,
    onlyRequests,
    onlyWithAddReasonDelayPreviousYear,
    onlyNotSchoolCandidate,
    onlyOutsideEnrollmentArea,
    onlyLanguageCourseRequired,
    onlyMinistryLanguageCourseRequired: false,
    onlyEnrollmentApproved,
    onlyEnrolled,
    outsideEnrollmentAreaStatus,
    onlyOutsideEnrollmentAreaOnlyEditable: false
  };
};

export const createInitialInstitutionIds = (
  currentRole: UserPermission | undefined
): number[] => {
  // location only represents an Institution if ClaimType = Institution
  return currentRole?.claimType === ClaimType.Institution &&
    !!currentRole?.locations?.[0]
    ? [currentRole.locations[0]]
    : ([] as number[]);
};

export const getInitialTableState = (storageKey: string) => {
  return TableUtils.CreateInitialTableState<RequestDataSearch>(
    storageKey,
    TableUtils.tableDefaultState(RequestSort.Code, ColumnSortEnum.DESC, 10)
  );
};

export const createEmptyPersonIdentification = (
  person?: PersonIdentification,
  oib?: string
): PersonIdentification => ({
  alternativeDocumentNumber: person?.alternativeDocumentNumber ?? null,
  alternativeDocumentTypeId: person?.alternativeDocumentTypeId ?? null,
  alternativeDocumentReasonId: person?.alternativeDocumentReasonId ?? null,
  oib: oib ?? person?.oib ?? undefined,
  firstName: person?.firstName ?? "",
  lastName: person?.lastName ?? "",
  dateOfBirth: person?.dateOfBirth ?? new Date(),
  isEmpty: !!person?.isEmpty,
  id: person?.id ?? 0,
});

export const getDefaultSolutionIdFromForm7 = (
  form7: Form7
): EnrollmentDecisionSolution | undefined => {
  if (!form7) return;

  const solutionMap = {
    isEarlyEnrollmentApproved:
      EnrollmentDecisionSolution.EarlyEnrollmentApproved,
    isEarlyEnrollmentRejected:
      EnrollmentDecisionSolution.EarlyEnrollmentRejected,
    isEnrollmentPostponement: EnrollmentDecisionSolution.EnrollmentPostponement,
    isTemporaryExemptionApproved:
      EnrollmentDecisionSolution.TemporaryExemptionApproved,
    isTemporaryExemptionRejected:
      EnrollmentDecisionSolution.TemporaryExemptionRejected,
    isAppropriateProgram: EnrollmentDecisionSolution.AppropriateProgram,
  };

  // Find first true flag and return corresponding solution
  const matchingFlag = Object.entries(solutionMap).find(
    ([key]) => form7[key as keyof Form7]
  );

  return matchingFlag ? matchingFlag[1] : undefined;
};
